import React from 'react';

/**
 * ChartRenderer - Detects and renders ASCII charts as visual charts
 * Falls back to original ASCII if detection fails
 */

// Detect ASCII line chart pattern
function detectLineChart(text) {
  const lines = text.split('\n');
  
  // Look for chart pattern: vertical axis with |, data points with *, goal lines with -
  const hasVerticalAxis = lines.some(line => /^\s*\d+\s*\|/.test(line));
  const hasDataPoints = text.includes('*');
  const hasAxisLabels = lines.some(line => /^\s*\+[-]+/.test(line));
  
  if (hasVerticalAxis && (hasDataPoints || hasAxisLabels)) {
    return parseLineChart(lines);
  }
  return null;
}

// Parse ASCII line chart into data structure
function parseLineChart(lines) {
  const dataPoints = [];
  const goalPoints = [];
  let yMax = 0;
  let yMin = Infinity;
  let xLabels = [];
  let goalValue = null;
  
  // First pass: find x-axis labels (look for lines with just numbers/spaces)
  lines.forEach(line => {
    // Look for x-axis labels (e.g., "     1   3   6   9   12  15  18")
    if (!/\|/.test(line) && /^\s*\d+/.test(line)) {
      const numbers = line.match(/\d+/g);
      if (numbers && numbers.length > 2) {
        xLabels = numbers.map(Number);
      }
    }
  });
  
  // Second pass: extract data points
  lines.forEach(line => {
    // Extract Y-axis value and data points
    const axisMatch = line.match(/^\s*(\d+(?:\.\d+)?)\s*\|(.*)/);
    if (axisMatch) {
      const yValue = parseFloat(axisMatch[1]);
      const dataLine = axisMatch[2];
      yMax = Math.max(yMax, yValue);
      yMin = Math.min(yMin, yValue);
      
      // Track character positions and build continuous lines
      for (let i = 0; i < dataLine.length; i++) {
        const char = dataLine[i];
        if (char === '*') {
          dataPoints.push({ x: i, y: yValue, type: 'actual' });
        } else if (char === '-') {
          goalPoints.push({ x: i, y: yValue, type: 'goal' });
        }
      }
    }
  });
  
  // Look for legend to extract goal value and metric names
  let actualMetric = 'Actual';
  let goalMetric = 'Goal';
  
  const legendLine = lines.find(line => /\(-\)\s*=\s*Goal/i.test(line) || /\(\*\)\s*=/i.test(line));
  if (legendLine) {
    // Extract goal value: "(-) = Goal (56.75)" or "(—) = Goal line"
    const goalMatch = legendLine.match(/Goal\s*\(?([\d.]+)\)?/i);
    if (goalMatch) {
      goalValue = parseFloat(goalMatch[1]);
    }
    
    // Extract actual metric name: "(*) = Actual defects/day" or "(*) = Actual PPH"
    // Handle both regular dash (-) and em-dash (—)
    const actualMatch = legendLine.match(/\(\*\)\s*=\s*Actual\s+(.+?)(?:\s*\([-—]\)|$)/i);
    if (actualMatch) {
      actualMetric = 'Actual ' + actualMatch[1].trim();
    }
    
    // Extract goal metric name if separate - handle both dash types
    const goalMetricMatch = legendLine.match(/\([-—]\)\s*=\s*(.+?)(?:\([\d.]+\)|$)/i);
    if (goalMetricMatch) {
      const extracted = goalMetricMatch[1].trim();
      if (extracted && !extracted.startsWith('Goal')) {
        goalMetric = extracted;
      }
    }
  }
  
  if (dataPoints.length === 0) return null;
  
  // Group points by x position to create continuous line
  const groupedData = {};
  const groupedGoal = {};
  
  dataPoints.forEach(p => {
    if (!groupedData[p.x]) groupedData[p.x] = [];
    groupedData[p.x].push(p.y);
  });
  
  goalPoints.forEach(p => {
    if (!groupedGoal[p.x]) groupedGoal[p.x] = [];
    groupedGoal[p.x].push(p.y);
  });
  
  return { 
    dataPoints, 
    goalPoints, 
    yMax, 
    yMin, 
    xLabels,
    goalValue,
    actualMetric,
    goalMetric,
    groupedData,
    groupedGoal
  };
}

// Detect bar chart with █ or ░ blocks — used for Pareto charts (L1–L3B).
// Time-series (linechart fence) takes the explicit lang='linechart' path instead.
function detectBarChart(text) {
  console.log('[ChartRenderer detectBarChart] Called with text length:', text.length);
  console.log('[ChartRenderer detectBarChart] First 200 chars:', text.substring(0, 200));

  // ASCII diagrams contain box-drawing or arrow chars — never interpret them as chart data.
  // This prevents the Lead Time Ladder (Section 6) and other box-art from misfiring as bar charts.
  if (/[\u250c\u2500\u2510\u2502\u2514\u2518\u251c\u2524\u252c\u2534\u253c\u2554\u2550\u2557\u2551\u255a\u255d\u25ba\u25bc\u25b2\u25c4]/.test(text)) {
    console.log('[ChartRenderer detectBarChart] Box-drawing/arrow chars detected — skipping, returning null');
    return null;
  }

  const lines = text.split('\n').filter(line => line.trim().length > 0);
  console.log('[ChartRenderer detectBarChart] Total non-empty lines:', lines.length);
  
  // Accept lines with | and either █, ░, or numeric values
  // Exclude markdown tables (lines with 3+ pipes or starting with |)
  const barLines = lines.filter(line => {
    const pipeCount = (line.match(/\|/g) || []).length;
    const isTable = pipeCount >= 3 || line.trim().startsWith('|');
    return !isTable && line.includes('|') && (line.includes('█') || line.includes('░') || /\|\s*[-+]?[\d.]+/.test(line));
  });
  
  console.log('[ChartRenderer detectBarChart] Found bar lines:', barLines.length);
  if (barLines.length > 0) {
    console.log('[ChartRenderer detectBarChart] First bar line:', barLines[0]);
  }
  
  if (barLines.length === 0) return null;
  
  // Extract title (first non-bar line)
  let title = null;
  const firstLine = lines[0];
  if (firstLine && !firstLine.includes('█') && !firstLine.includes('░') && !firstLine.includes('|')) {
    title = firstLine.replace(/^#+\s*/, '').trim();
  }

  const bars = [];
  barLines.forEach(line => {
    // Match pattern: "Label | █/░ Value Unit" or "Label |  Value Unit"
    // Support negative numbers (e.g., -7.5 PPH/day)
    const match = line.match(/^([^|]+)\|\s*([█░\s]*)\s*([-+]?[\d.]+)(?:\s+(.+))?$/);
    if (match) {
      const label = match[1].trim();
      const value = parseFloat(match[3]);
      const unit = match[4] ? match[4].trim() : '';  
      
      if (!isNaN(value) && label) {
        bars.push({ label, value, unit });
        console.log('[ChartRenderer detectBarChart] Parsed bar:', { label, value, unit });
      }
    } else {
      console.log('[ChartRenderer detectBarChart] Failed to match line:', line);
    }
  });
  
  console.log('[ChartRenderer detectBarChart] Total bars parsed:', bars.length);
  
  if (bars.length === 0) return null;
  
  // Pareto sort: descending by absolute value so largest gap appears first.
  bars.sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
  
  // Calculate max absolute value for percentage scaling
  const maxValue = Math.max(...bars.map(b => Math.abs(b.value)));
  
  console.log('[ChartRenderer detectBarChart] Returning chart data with', bars.length, 'bars, title:', title);
  return { bars, maxValue, title };
}

function isDateLikeTrendLabel(label) {
  const trimmed = String(label || '').trim();
  if (!trimmed) return false;

  return /^(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)(?:\s+\d{4})?$/i.test(trimmed) ||
    /^\d{1,2}\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)$/i.test(trimmed) ||
    /^\d{4}-\d{2}-\d{2}$/.test(trimmed);
}

function isTrendFenceData(chartData) {
  if (!chartData || !Array.isArray(chartData.bars) || chartData.bars.length < 2) return false;

  const cleanTitle = String(chartData.title || '').replace(/\*\*/g, '').trim();
  if (chartData.goalValue != null) return true;
  if (/\btrend\b/i.test(cleanTitle) && !/\bhour(?:ly)?\b/i.test(cleanTitle)) return true;

  return chartData.bars.length >= 4 && chartData.bars.every((bar) => isDateLikeTrendLabel(bar.label));
}

// Render SVG line chart
function renderLineChart(chartData) {
  const { dataPoints, goalPoints, yMax, yMin, xLabels, goalValue, actualMetric, goalMetric } = chartData;
  const width = 600;
  const height = 250;
  const padding = { top: 20, right: 20, bottom: 50, left: 50 };
  
  // Calculate max x position
  const maxX = Math.max(
    ...dataPoints.map(p => p.x),
    ...(goalPoints.length > 0 ? goalPoints.map(p => p.x) : [0])
  );
  
  // Scale functions
  const xScale = (x) => padding.left + (x * (width - padding.left - padding.right)) / maxX;
  const yScale = (y) => height - padding.bottom - ((y - yMin) * (height - padding.top - padding.bottom)) / (yMax - yMin);
  
  // Generate SVG path for data points
  const dataPath = dataPoints
    .sort((a, b) => a.x - b.x)
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xScale(p.x)} ${yScale(p.y)}`)
    .join(' ');
  
  // Generate SVG path for goal line if exists
  const goalPath = goalPoints.length > 0
    ? goalPoints
        .sort((a, b) => a.x - b.x)
        .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xScale(p.x)} ${yScale(p.y)}`)
        .join(' ')
    : null;
  
  // Determine x-axis label positions
  const xAxisLabels = xLabels.length > 0 
    ? xLabels.map((label, i) => ({
        value: label,
        x: padding.left + (i * (width - padding.left - padding.right)) / (xLabels.length - 1)
      }))
    : [];
  
  const hasGoalLine = goalPoints.length > 0;
  
  return (
    <div className="chart-container">
      <div style={{ width: '100%' }}>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" style={{ display: 'block', height: 'auto' }}>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = padding.top + ratio * (height - padding.top - padding.bottom);
          const yValue = yMin + (1 - ratio) * (yMax - yMin);
          return (
            <g key={ratio}>
              <line
                x1={padding.left}
                y1={y}
                x2={width - padding.right}
                y2={y}
                stroke="#e0e0e0"
                strokeWidth="1"
              />
              {/* Y-axis labels */}
              <text 
                x={padding.left - 8} 
                y={y + 4} 
                textAnchor="end" 
                fontSize="11" 
                fill="#666"
              >
                {yValue.toFixed(1)}
              </text>
            </g>
          );
        })}
        
        {/* Y-axis */}
        <line
          x1={padding.left}
          y1={padding.top}
          x2={padding.left}
          y2={height - padding.bottom}
          stroke="#333"
          strokeWidth="2"
        />
        
        {/* X-axis */}
        <line
          x1={padding.left}
          y1={height - padding.bottom}
          x2={width - padding.right}
          y2={height - padding.bottom}
          stroke="#333"
          strokeWidth="2"
        />
        
        {/* X-axis labels */}
        {xAxisLabels.map((label, i) => (
          <text
            key={i}
            x={label.x}
            y={height - padding.bottom + 20}
            textAnchor="middle"
            fontSize="11"
            fill="#666"
          >
            {label.value}
          </text>
        ))}
        
        {/* Goal line (dashed red) */}
        {goalPath && (
          <path
            d={goalPath}
            stroke="#ef4444"
            strokeWidth="2.5"
            strokeDasharray="6,4"
            fill="none"
          />
        )}
        
        {/* Actual data line (solid blue) */}
        <path
          d={dataPath}
          stroke="#3b82f6"
          strokeWidth="2.5"
          fill="none"
        />
        
        {/* Data point circles */}
        {dataPoints.map((p, i) => (
          <circle
            key={`data-${i}`}
            cx={xScale(p.x)}
            cy={yScale(p.y)}
            r="4"
            fill="#3b82f6"
          />
        ))}
        
        {/* Goal point circles */}
        {goalPoints.map((p, i) => (
          <circle
            key={`goal-${i}`}
            cx={xScale(p.x)}
            cy={yScale(p.y)}
            r="4"
            fill="#ef4444"
          />
        ))}
      </svg>
      </div>
      
      {/* Visual Legend */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '20px',
        marginTop: '12px',
        fontSize: '13px',
        color: '#555'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{
            width: '24px',
            height: '3px',
            backgroundColor: '#3b82f6',
            borderRadius: '2px'
          }} />
          <span>{actualMetric || 'Actual'}</span>
        </div>
        {hasGoalLine && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{
              width: '24px',
              height: '3px',
              background: 'repeating-linear-gradient(90deg, #ef4444 0, #ef4444 6px, transparent 6px, transparent 10px)',
              borderRadius: '2px'
            }} />
            <span>{goalMetric || 'Goal'}{goalValue ? ` (${goalValue})` : ''}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Render SVG line chart for trend data (isTrend=true path)
function renderTrendLineChart(chartData) {
  const { bars, title, goalValue } = chartData;

  const width = 600;
  const height = 250;
  const pad = { top: 30, right: 30, bottom: 50, left: 60 };
  const chartW = width - pad.left - pad.right;
  const chartH = height - pad.top - pad.bottom;

  const values = bars.map(b => b.value);
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  // Y range: include goal if present; add 5% padding above/below
  const yMin = Math.floor(Math.min(rawMin, goalValue ?? rawMin) * 0.97);
  const yMax = Math.ceil(Math.max(rawMax, goalValue ?? rawMax) * 1.03);

  const xScale = i => pad.left + (bars.length > 1 ? (i / (bars.length - 1)) * chartW : chartW / 2);
  const yScale = v => pad.top + chartH - ((v - yMin) / (yMax - yMin)) * chartH;

  // Actual value polyline
  const polyline = bars.map((b, i) => `${xScale(i)},${yScale(b.value)}`).join(' ');

  // 5 y-axis ticks
  const yTicks = Array.from({ length: 5 }, (_, i) => yMin + Math.round(((yMax - yMin) * i) / 4));

  const cleanTitle = (title || '').replace(/\*\*/g, '').trim();

  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} style={{ fontFamily: 'sans-serif', overflow: 'visible' }}>
      {/* Title */}
      {cleanTitle && (
        <text x={width / 2} y={pad.top - 10} textAnchor="middle" fontSize="13" fontWeight="600" fill="#1f2937">
          {cleanTitle}
        </text>
      )}

      {/* Y-axis grid + ticks */}
      {yTicks.map(v => (
        <g key={v}>
          <line x1={pad.left} y1={yScale(v)} x2={width - pad.right} y2={yScale(v)}
            stroke="#e5e7eb" strokeWidth="1" />
          <text x={pad.left - 6} y={yScale(v) + 4} textAnchor="end" fontSize="11" fill="#6b7280">
            {v}
          </text>
        </g>
      ))}

      {/* Goal dashed line */}
      {goalValue != null && (
        <>
          <line x1={pad.left} y1={yScale(goalValue)} x2={width - pad.right} y2={yScale(goalValue)}
            stroke="#ef4444" strokeWidth="2" strokeDasharray="6,4" />
          <text x={width - pad.right + 4} y={yScale(goalValue) + 4} fontSize="10" fill="#ef4444">
            Goal
          </text>
        </>
      )}

      {/* Actual polyline */}
      <polyline points={polyline} fill="none" stroke="#3b82f6" strokeWidth="2.5" strokeLinejoin="round" />

      {/* Data points + value labels */}
      {bars.map((b, i) => (
        <g key={i}>
          <circle cx={xScale(i)} cy={yScale(b.value)} r="4" fill="#3b82f6" />
          <text x={xScale(i)} y={yScale(b.value) - 8} textAnchor="middle" fontSize="10" fill="#374151">
            {b.value}
          </text>
        </g>
      ))}

      {/* X-axis labels */}
      {bars.map((b, i) => (
        <text key={i} x={xScale(i)} y={height - pad.bottom + 18} textAnchor="middle" fontSize="11" fill="#6b7280">
          {b.label}
        </text>
      ))}

      {/* Axes */}
      <line x1={pad.left} y1={pad.top} x2={pad.left} y2={pad.top + chartH} stroke="#9ca3af" strokeWidth="1" />
      <line x1={pad.left} y1={pad.top + chartH} x2={width - pad.right} y2={pad.top + chartH} stroke="#9ca3af" strokeWidth="1" />

      {/* Legend */}
      <circle cx={pad.left} cy={height - 8} r="4" fill="#3b82f6" />
      <text x={pad.left + 8} y={height - 4} fontSize="11" fill="#6b7280">Actual</text>
      {goalValue != null && (
        <>
          <line x1={pad.left + 60} y1={height - 8} x2={pad.left + 80} y2={height - 8}
            stroke="#ef4444" strokeWidth="2" strokeDasharray="4,3" />
          <text x={pad.left + 84} y={height - 4} fontSize="11" fill="#6b7280">Target: {goalValue}</text>
        </>
      )}
    </svg>
  );
}

// Render CSS bar chart (Pareto) — horizontal bars, largest value first
function renderBarChart(chartData) {
  const { bars, maxValue, title } = chartData;
  console.log('[ChartRenderer renderBarChart] Called with:', { barCount: bars.length, maxValue, title });
  console.log('[ChartRenderer renderBarChart] First bar:', JSON.stringify(bars[0]));

  return (
    <div className="chart-container">
      {title && (
        <div style={{
          fontSize: '14px',
          fontWeight: '600',
          marginBottom: '16px',
          color: '#1f2937',
          textAlign: 'center'
        }}>
          {title}
        </div>
      )}
      <div className="bar-chart-container">
        {bars.map((bar, i) => {
          const percentage = (Math.abs(bar.value) / maxValue) * 100;
          return (
            <div key={i} className="bar-chart-row">
              <div className="bar-chart-label">{bar.label}</div>
              <div className="bar-chart-bar-wrapper">
                <div
                  className="bar-chart-bar"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <div className="bar-chart-value">
                {bar.value} {bar.unit}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Parse a linechart fence body into { bars, title, goalValue } for renderTrendLineChart.
// Format (same as _buildTrendFence output):
//   Title Line
//   Goal: N          (optional)
//   Jan | 91.93
//   Feb | 91.77
function parseLinechartFence(text) {
  const lines = text.split('\n').filter(l => l.trim().length > 0);
  if (!lines.length) return null;

  let title = null;
  let goalValue = null;
  const bars = [];

  for (const line of lines) {
    const trimmed = line.trim();
    // Goal header
    if (/^Goal:\s*[-\d.]+/i.test(trimmed)) {
      const gv = parseFloat(trimmed.replace(/^Goal:\s*/i, ''));
      if (!isNaN(gv)) goalValue = gv;
      continue;
    }
    // Data row: "Label | value"
    const pipeIdx = trimmed.indexOf('|');
    if (pipeIdx !== -1) {
      const label = trimmed.slice(0, pipeIdx).trim();
      const valuePart = trimmed.slice(pipeIdx + 1).trim();
      // Strip any block chars left from the legacy path, keep the number
      const valMatch = valuePart.match(/([█░\s]*)([-\d.]+)/);
      if (valMatch) {
        const value = parseFloat(valMatch[2]);
        if (!isNaN(value) && label) bars.push({ label, value, unit: '' });
      }
      continue;
    }
    // First non-goal, non-data line is the title
    if (!title) title = trimmed.replace(/\*\*/g, '');
  }

  if (!bars.length) return null;
  return { bars, title, goalValue };
}

// Main component
export default function ChartRenderer({ content, lang }) {
  console.log('[ChartRenderer] Received content, length:', content.length, 'lang:', lang);
  console.log('[ChartRenderer] Preview:', content.substring(0, 100));

  const parsedTrendFence = parseLinechartFence(content);

  // ── linechart fence: explicit time-series from backend ────────────────────
  if ((lang === 'linechart' || isTrendFenceData(parsedTrendFence)) && parsedTrendFence) {
    console.log('[ChartRenderer] Rendering trend line chart from fence data');
    return (
      <div className="chart-wrapper">
        {renderTrendLineChart(parsedTrendFence)}
        <details style={{ marginTop: '8px' }}>
          <summary style={{ cursor: 'pointer', fontSize: '0.85em', color: '#666' }}>View raw data</summary>
          <pre style={{ fontSize: '0.8em', backgroundColor: '#f5f5f5', padding: '8px', borderRadius: '4px', overflowX: 'auto', marginTop: '4px' }}>{content}</pre>
        </details>
      </div>
    );
  }

  // Try to detect and render charts
  const lineChartData = detectLineChart(content);
  if (lineChartData) {
    console.log('[ChartRenderer] ✓ Detected line chart');
    return (
      <div className="chart-wrapper">
        {renderLineChart(lineChartData)}
        <details style={{ marginTop: '8px' }}>
          <summary style={{ cursor: 'pointer', fontSize: '0.85em', color: '#666' }}>
            View raw data
          </summary>
          <pre style={{ 
            fontSize: '0.8em', 
            backgroundColor: '#f5f5f5', 
            padding: '8px', 
            borderRadius: '4px',
            overflowX: 'auto',
            marginTop: '4px'
          }}>
            {content}
          </pre>
        </details>
      </div>
    );
  }
  
  const barChartData = detectBarChart(content);
  console.log('[ChartRenderer] detectBarChart returned:', barChartData ? 'data with ' + barChartData.bars.length + ' bars' : 'null');
  if (barChartData) {
    console.log('[ChartRenderer] Rendering pareto bar chart with title:', barChartData.title);
    return (
      <div className="chart-wrapper">
        {renderBarChart(barChartData)}
        <details style={{ marginTop: '8px' }}>
          <summary style={{ cursor: 'pointer', fontSize: '0.85em', color: '#666' }}>
            View raw data
          </summary>
          <pre style={{ 
            fontSize: '0.8em', 
            backgroundColor: '#f5f5f5', 
            padding: '8px', 
            borderRadius: '4px',
            overflowX: 'auto',
            marginTop: '4px'
          }}>
            {content}
          </pre>
        </details>
      </div>
    );
  }
  
  // Fallback: return original content wrapped in <pre>
  return (
    <pre style={{ 
      fontSize: '0.85em', 
      whiteSpace: 'pre-wrap', 
      overflowX: 'auto',
      margin: '8px 0'
    }}>
      {content}
    </pre>
  );
}
